import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import '../lib/charts';
import { SERIES } from '../lib/charts';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, int, niceDate } from '../lib/format';
import { Card, Spinner, ErrorBox, Empty, DataTable, Delta } from '../components/ui';

// Side-by-side performance of named store groups — Edmonton vs Calgary,
// province vs province, or any custom grouping from tenants.json.
export default function GroupComparisonPage() {
  const { groups, startDate, endDate, compareMode } = useFilters();

  // Every selectable group, keyed so custom/city/province names can't collide.
  const available = useMemo(() => ([
    ...groups.custom.map(g => ({ ...g, key: `custom:${g.name}` })),
    ...groups.city.map(g => ({ ...g, key: `city:${g.name}` })),
    ...groups.province.map(g => ({ ...g, key: `prov:${g.name}`, name: g.name })),
  ]), [groups]);

  // Default: compare the cities (that's the headline use case).
  const [selectedKeys, setSelectedKeys] = useState(null); // null until groups load
  const effectiveKeys = useMemo(() => {
    if (selectedKeys) return selectedKeys;
    return groups.city.filter(g => g.locations.length > 0).slice(0, 6).map(g => `city:${g.name}`);
  }, [selectedKeys, groups]);

  const chosen = available.filter(g => effectiveKeys.includes(g.key));

  const { data, loading, error } = useReport('group-comparison', {
    startDate,
    endDate,
    compare: compareMode,
    groups: JSON.stringify(chosen.map(g => ({ name: g.name, locations: g.locations }))),
  }, { enabled: chosen.length > 0 });

  const toggle = (key) => {
    setSelectedKeys(prev => {
      const cur = prev ?? effectiveKeys;
      return cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
    });
  };

  const rows = data || [];
  const maxPerStore = Math.max(1, ...rows.map(r => r.perStoreNet));

  const chart = {
    labels: rows.map(r => r.name),
    datasets: [{
      label: 'Net sales per store',
      data: rows.map(r => r.perStoreNet),
      backgroundColor: SERIES[0],
      borderRadius: 4,
      maxBarThickness: 40,
    }],
  };

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Card
        tour="grp-picker"
        title="Compare Groups"
        sub="pick two or more groups — cities and provinces are automatic, custom groups come from the tenant config"
      >
        {available.length === 0 ? <Empty>No groups available yet.</Empty> : (
          <div className="chip-row">
            {available.map(g => (
              <button
                key={g.key}
                className={`chip ${effectiveKeys.includes(g.key) ? 'active' : ''}`}
                title={`${g.locations.length} stores`}
                onClick={() => toggle(g.key)}
              >
                {g.name} ({g.locations.length})
              </button>
            ))}
          </div>
        )}
      </Card>

      {chosen.length === 0 ? (
        <Empty>Select at least one group above.</Empty>
      ) : loading ? <Spinner label="Comparing groups…" /> : error ? <ErrorBox message={error} /> : rows.length === 0 ? <Empty /> : (
        <>
          <Card
            tour="grp-chart"
            title="Net Sales per Store"
            sub={`${niceDate(startDate)} – ${niceDate(endDate)} · per-store average keeps different-sized groups comparable`}
          >
            <div className="chart-box short">
              <Bar
                data={chart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: ctx => {
                          const r = rows[ctx.dataIndex];
                          return ` ${money(r.perStoreNet)} avg per store · ${money(r.netSales)} total across ${r.storeCount}`;
                        },
                      },
                    },
                  },
                  scales: {
                    x: { ticks: { callback: v => money(v, { cents: false }) }, beginAtZero: true },
                    y: { grid: { display: false } },
                  },
                  indexAxis: 'y',
                }}
              />
            </div>
          </Card>

          <Card tour="grp-table" title="Group Scorecard" sub={compareMode === 'priorYear' ? 'vs same period last year (weekday-aligned)' : 'vs prior period of equal length'}>
            <DataTable
              csvName="group-comparison.csv"
              defaultSort={{ key: 'perStoreNet', dir: 'desc' }}
              columns={[
                { key: 'name', label: 'Group' },
                { key: 'storeCount', label: 'Stores', num: true, render: r => int(r.storeCount), csv: r => r.storeCount },
                { key: 'netSales', label: 'Net Sales', num: true, render: r => money(r.netSales), csv: r => (r.netSales / 100).toFixed(2) },
                {
                  key: 'vsPrior', label: compareMode === 'priorYear' ? 'vs LY' : 'vs Prior', num: true,
                  sortValue: r => (r.prevNetSales ? (r.netSales - r.prevNetSales) / r.prevNetSales : -Infinity),
                  render: r => <Delta curr={r.netSales} prev={r.prevNetSales} />,
                  csv: r => r.prevNetSales ? (((r.netSales - r.prevNetSales) / r.prevNetSales) * 100).toFixed(1) : '',
                },
                {
                  key: 'perStoreNet', label: 'Per Store', num: true,
                  render: r => (
                    <>
                      {money(r.perStoreNet)}{' '}
                      <span className="bar" style={{ width: 60 * (r.perStoreNet / maxPerStore), marginLeft: 6 }} />
                    </>
                  ),
                  csv: r => (r.perStoreNet / 100).toFixed(2),
                },
                { key: 'orders', label: 'Orders', num: true, render: r => int(r.orders), csv: r => r.orders },
                { key: 'avgOrderValue', label: 'AOV', num: true, render: r => money(r.avgOrderValue), csv: r => (r.avgOrderValue / 100).toFixed(2) },
                { key: 'tips', label: 'Tips', num: true, render: r => money(r.tips), csv: r => (r.tips / 100).toFixed(2) },
                {
                  key: 'salesPerLaborHour', label: '$ / Labor Hr', num: true,
                  render: r => r.salesPerLaborHour != null ? money(r.salesPerLaborHour) : '—',
                  csv: r => r.salesPerLaborHour != null ? (r.salesPerLaborHour / 100).toFixed(2) : '',
                },
              ]}
              rows={rows.map(r => ({ ...r, __key: r.name }))}
            />
          </Card>
        </>
      )}
    </div>
  );
}
